/**
 * Copyright © Veeam Software Group GmbH.
 */

import React, { useEffect, useMemo, useRef } from 'react';
import {
    ColumnTexts,
    Icon,
    ICON_SIZES,
    StackView,
    STACK_ALIGN,
    STACK_DISTRIBUTION,
    STACK_GAP,
    Text,
    TEXT_SIZE,
} from '@veeam-vspc/components';
import { AzureWorkloads, ProtectedDataFilterBy, ProtectionType } from '@veeam-vspc/models/web-controllers';
import { observer } from 'mobx-react-lite';

import type { BaseProtectedDataWorkloadOverviewAzureWorkloads, ProtectedDataOverviewRequest } from '@veeam-vspc/models/web-controllers';

import { TableWidget } from 'views/components/TableWidget';
import { NameColumn } from 'views/components/TableWidget/components/NameColumn';
import successIcon from 'images/success.svg';
import filesIcon from 'images/pages/protected-data-summary/azure-files.svg';
import sqlIcon from 'images/pages/protected-data-summary/azure-sql.svg';
import vmIcon from 'images/pages/protected-data-summary/azure-vm.svg';
import notConnectedIcon from 'images/pages/protected-data-summary/azure-not-connected.svg';
import { useLang } from 'views/providers/LangProvider/hooks';
import { useIntervalRequest } from 'views/hooks';
import { NoInfoColumn } from 'views/components/TableWidget/components/NoInfoColumn';
import { useAppStore } from 'views/providers/AppProvider/hooks';
import { getHelpResourceLink } from 'core/utils';
import { useProtectedDataSummaryStore } from '../../store';

const icons = {
    [AzureWorkloads.Files]: filesIcon,
    [AzureWorkloads.SqlDatabases]: sqlIcon,
    [AzureWorkloads.VirtualMachines]: vmIcon,
};

export const Azure: React.FC = observer(() => {
    const store = useProtectedDataSummaryStore();
    const lang = useLang();
    const didMount = useRef(false);
    const abortController = useRef(new AbortController());
    const { portalUser } = useAppStore();

    const names = useMemo(() => ({
        [AzureWorkloads.Files]: lang.AZURE_FILES,
        [AzureWorkloads.SqlDatabases]: lang.AZURE_SQL,
        [AzureWorkloads.VirtualMachines]: lang.VIRTUAL_MACHINES,
    }), []);

    const request = useIntervalRequest<ProtectedDataOverviewRequest, BaseProtectedDataWorkloadOverviewAzureWorkloads[]>(
        '/ProtectedOverview/Azure',
        {
            rpoDays: store.rpoDays,
            filterType: ProtectedDataFilterBy.Rpo,
        },
        {
            signal: abortController.current.signal,
        });

    useEffect(() => {
        if (didMount.current) {
            abortController.current.abort();

            abortController.current = new AbortController();
            return request.forceRequest();
        }

        didMount.current = true;
    }, [store.rpoDays]);

    const isResellerUser = portalUser.isServiceProviderGroup();
    const noDataUrl = isResellerUser ? getHelpResourceLink('656') : getHelpResourceLink('655');

    const items = request.isError || !Array.isArray(request.data)
        ? []
        : (request.data as BaseProtectedDataWorkloadOverviewAzureWorkloads[]);

    return (
        <TableWidget
            data={items}
            columns={[
                {
                    name: lang.MICROSOFT_AZURE,
                    cellRenderer: ({ rowData, key }) => (
                        <NameColumn
                            icon={icons[rowData.workloadType]}
                            name={names[rowData.workloadType]}
                            key={key}
                        />
                    ),
                },
                {
                    name: lang.SNAPSHOTS,
                    cellRenderer: ({ rowData, key }) => {
                        const snapshots = rowData.protectionData.find(x => x.protectionType === ProtectionType.Snapshot);
                        const protectedCount = snapshots.protectedCount.value;
                        const overallCount = snapshots.overallCount;

                        if (rowData.workloadType === AzureWorkloads.SqlDatabases) {
                            return (
                                <StackView key={key} distribution={STACK_DISTRIBUTION.center}>
                                    {ColumnTexts.DashSymbol}
                                </StackView>
                            );
                        }

                        if (overallCount === 0) {
                            return (
                                <StackView
                                    align={STACK_ALIGN.center}
                                    gap={STACK_GAP.s}
                                    key={key}
                                >
                                    <NoInfoColumn />
                                </StackView>
                            );
                        }

                        if (protectedCount !== overallCount) {
                            return (
                                <StackView
                                    align={STACK_ALIGN.end}
                                    gap={STACK_GAP.s}
                                    key={key}
                                >
                                    <Text size={TEXT_SIZE.xl}>{protectedCount}</Text>
                                    <Text size={TEXT_SIZE.l}>of</Text>
                                    <Text size={TEXT_SIZE.xl}>{overallCount}</Text>
                                </StackView>
                            );
                        }

                        return (
                            <StackView
                                align={STACK_ALIGN.center}
                                gap={STACK_GAP.s}
                                key={key}
                            >
                                <Icon src={successIcon} size={ICON_SIZES.xl} />
                                <Text size={TEXT_SIZE.xl}>{overallCount}</Text>
                            </StackView>
                        );
                    },
                },
                {
                    name: lang.BACKUPS,
                    cellRenderer: ({ rowData, key }) => {
                        const snapshots = rowData.protectionData.find(x => x.protectionType === ProtectionType.Backup);
                        const protectedCount = snapshots.protectedCount.value;
                        const overallCount = snapshots.overallCount;

                        if (rowData.workloadType === AzureWorkloads.Files) {
                            return (
                                <StackView key={key} distribution={STACK_DISTRIBUTION.center}>
                                    {ColumnTexts.DashSymbol}
                                </StackView>
                            );
                        }

                        if (overallCount === 0) {
                            return (
                                <StackView
                                    align={STACK_ALIGN.center}
                                    gap={STACK_GAP.s}
                                    key={key}
                                >
                                    <NoInfoColumn />
                                </StackView>
                            );
                        }

                        if (protectedCount !== overallCount) {
                            return (
                                <StackView
                                    align={STACK_ALIGN.end}
                                    gap={STACK_GAP.s}
                                    key={key}
                                >
                                    <Text size={TEXT_SIZE.xl}>{protectedCount}</Text>
                                    <Text size={TEXT_SIZE.l}>of</Text>
                                    <Text size={TEXT_SIZE.xl}>{overallCount}</Text>
                                </StackView>
                            );
                        }

                        return (
                            <StackView
                                align={STACK_ALIGN.center}
                                gap={STACK_GAP.s}
                                key={key}
                            >
                                <Icon src={successIcon} size={ICON_SIZES.xl} />
                                <Text size={TEXT_SIZE.xl}>{overallCount}</Text>
                            </StackView>
                        );
                    },
                },
            ]}
            loading={request.loading}
            noDataConfig={{
                linkText: lang.START_USING_VEEAM_BACKUP_FOR_MICROSOFT_AZURE,
                icon: notConnectedIcon,
                linkHandler: () => window.open(noDataUrl, '_blank', 'noopener, noreferrer'),
            }}
        />
    );
});
